import { ToolPolicy, ToolWeight, allTools, resolveToolPolicy, temporarilyDisabledTools, toolRegistry, } from '@debbie/common/dist/agents/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { agentPoliciesCache, useToolPolicies, } from '@debbie/cortex/dist/agent-policies/index.js';
import * as api from '@debbie/cortex/dist/api/agent-policies.js';
import { h } from '@debbie/cortex/dist/index.js';
import React, { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { errorNotification, successNotification } from '../../utils/errors.js';
import Errors from '../Errors.js';
import Loading from '../Loading.js';
import Panel from '../Panel.js';
import PlaceholderLoader from '../PlaceholderLoader.js';
const Note = styled.p `
  color: #6b6b6b;
  font-size: 13px;
  margin: 0 0 16px 0;
`;
const Table = styled.div `
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const Row = styled.div `
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  align-items: start;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px 14px;
`;
const Left = styled.div `
  min-width: 0;
`;
const Name = styled.div `
  font-weight: 600;
  font-size: 14px;
`;
const Description = styled.div `
  color: #6b6b6b;
  font-size: 12px;
  margin-top: 4px;
`;
const RadioRow = styled.div `
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 200px;
`;
const RadioLabel = styled.label `
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  cursor: pointer;
  user-select: none;

  > input {
    margin: 0;
  }
`;
const DefaultHint = styled.span `
  color: #6b6b6b;
  font-size: 11px;
  margin-left: 6px;
`;
const POLICY_OPTIONS = [
    ToolPolicy.Disabled,
    ToolPolicy.RequireApproval,
];
const HEAVY_TOOLS = allTools.filter((t) => t.weight === ToolWeight.Heavy && !temporarilyDisabledTools.has(t.name));
const AgentSettings = () => {
    const lang = useSelector((s) => s.lang);
    const loadable = useToolPolicies();
    const policies = h.hasData(loadable) ? loadable.data : null;
    const mutationInFlight = useRef(false);
    const [mutatingToolName, setMutatingToolName] = useState(null);
    const finishMutation = () => {
        mutationInFlight.current = false;
        setMutatingToolName(null);
    };
    const runMutation = (toolName, mutate) => {
        if (mutationInFlight.current) {
            return;
        }
        mutationInFlight.current = true;
        setMutatingToolName(toolName);
        try {
            mutate();
        }
        catch (error) {
            finishMutation();
            throw error;
        }
    };
    const setMutation = useMutation(api.setPolicy, {
        onSuccess: () => {
            agentPoliciesCache.invalidate.create();
            successNotification(lang, 'update', lang.agentSettings.title);
        },
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.agentSettings.title);
        },
        onSettled: finishMutation,
    });
    const resetMutation = useMutation(api.resetPolicy, {
        onSuccess: () => {
            agentPoliciesCache.invalidate.create();
            successNotification(lang, 'update', lang.agentSettings.title);
        },
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.agentSettings.title);
        },
        onSettled: finishMutation,
    });
    const overrideByTool = useMemo(() => new Map((policies ?? []).map((p) => [p.toolName, p.policy])), [policies]);
    if (h.isError(loadable)) {
        return (React.createElement(Panel, { header: lang.agentSettings.title },
            React.createElement(Errors, { errors: [loadable.error.message] })));
    }
    if (!policies) {
        return (React.createElement(Panel, { header: lang.agentSettings.title },
            React.createElement(PlaceholderLoader, null)));
    }
    return (React.createElement(Panel, { header: lang.agentSettings.title },
        React.createElement(Note, null, lang.agentSettings.description),
        React.createElement(Table, null, HEAVY_TOOLS.map((tool) => {
            const override = overrideByTool.get(tool.name) ?? null;
            const configured = resolveToolPolicy(toolRegistry[tool.name], override);
            // Historical AUTO_RUN overrides are treated as approval-required.
            // The migration removes them, but this keeps mixed-version deploys safe.
            const effective = configured === ToolPolicy.AutoRun
                ? ToolPolicy.RequireApproval
                : configured;
            return (React.createElement(Row, { key: tool.name },
                React.createElement(Left, null,
                    React.createElement(Name, null, lang.agentToolLabels[tool.name]),
                    React.createElement(Description, null, lang.agentToolDescriptions[tool.name])),
                React.createElement(RadioRow, null,
                    POLICY_OPTIONS.map((policy) => {
                        const isSelected = effective === policy;
                        const isDefault = override === null && tool.defaultPolicy === policy;
                        return (React.createElement(RadioLabel, { key: policy },
                            React.createElement("input", { type: "radio", name: `policy-${tool.name}`, value: policy, checked: isSelected, disabled: setMutation.isLoading || resetMutation.isLoading, onChange: () => {
                                    if (policy === tool.defaultPolicy) {
                                        runMutation(tool.name, () => resetMutation.mutate(tool.name));
                                    }
                                    else {
                                        runMutation(tool.name, () => setMutation.mutate(tool.name, policy));
                                    }
                                } }),
                            React.createElement("span", null,
                                lang.agentSettings.policies[policy],
                                tool.defaultPolicy === policy && (React.createElement(DefaultHint, null,
                                    "(",
                                    lang.agentSettings.default,
                                    ")")))));
                    }),
                    mutatingToolName === tool.name && React.createElement(Loading, { width: "14px" }))));
        }))));
};
export default AgentSettings;
//# sourceMappingURL=index.js.map